<template>
  <div class="authoring-wrapper">
    <div class="flex h-full">
      <ProgressBar
        :stepIndex="3"
        :surveyID="surveyID"
        stepsType="map-maker"
        :conciergeAssistance="false"
        :progressBarStatus="progressBarStatus"
      />
      <section class="wrapper">
        <a-breadcrumb class="breadcrum breadcrum--authoring">
          <template v-if="user !== null && user.SkinIdentifier !== 'pg'">
            <a-breadcrumb-item
              ><a href="/">{{ client.ClientName }}</a></a-breadcrumb-item
            >
            <a-breadcrumb-item
              ><a href="/products">Products</a></a-breadcrumb-item
            >
            <a-breadcrumb-item>Stimuli</a-breadcrumb-item>
          </template>
        </a-breadcrumb>
        <div class="wrapper__inner">
          <div class="page-title">
            <icon-base
              :width="32"
              :height="33"
              :viewBox1="32"
              :viewBox2="33"
              iconName="chart"
              iconColor="transparent"
              ><icon-chart-histogram
            /></icon-base>
            <div class="page-title__text">
              <h1>Stimuli</h1>
              <span>{{ pageSubtitle }}</span>
            </div>
          </div>
          <!-- STEP: BRANDS -->
          <div class="box-wrapper" v-if="activeStepKey === 'brands'">
            <StimuliTitle
              :title="`Select the ${entityType} that you want to run maps for. A cell will be created for each ${entityType} you select. Please ensure you upload the image that you want respondents to see for the exercise.`"
            />
            <Loader
              class="mt-35"
              text="Loading brands..."
              v-if="loadingBrands"
            />
            <div v-else class="management-system__grid">
              <BrandItem
                v-for="(item, index) in brands"
                :key="index"
                :brand="item"
                :loading="loadingBrands"
                @item-click="selectBrand(index)"
              />
            </div>
          </div>

          <!-- STEP: MEDIA IMAGE (placeholder) -->
          <div class="box-wrapper" v-else-if="activeStepKey === 'media-image'">
            <StimuliTitle title="Please upload advertising image." />
            <div class="p-6 border rounded-lg">
              <!-- if personaType is new and cellType is multi then show ChooseBrandAdItem component else show UploadAdvertisingImage -->
              <template v-if="personaType === 'new' && cellType === 'multi'">
                <ChooseBrandAdItem
                  v-for="(item, index) in brands"
                  :key="index"
                  :brand="item"
                  :surveyID="surveyID"
                  :mediaType="'image'"
                  @set-brand-ad-image="setBrandAdImage($event, index)"
                  v-show="item.IsSelected"
                />
              </template>
              <template v-else>
                <UploadAdvertisingImage
                  :surveyID="surveyID"
                  @set-image-url="imageURL = $event"
                  :imageURL="imageURL"
                />
              </template>
            </div>
          </div>

          <!-- STEP: MEDIA VIDEO (placeholder) -->
          <div class="box-wrapper" v-else-if="activeStepKey === 'media-video'">
            <StimuliTitle title="Please upload advertising video." />
            <div class="p-6 border rounded-lg">
              <!-- if personaType is new and cellType is multi then show ChooseBrandAdItem component else show UploadAdvertisingImage -->
              <template v-if="personaType === 'new' && cellType === 'multi'">
                <ChooseBrandAdItem
                  v-for="(item, index) in brands"
                  :key="index"
                  :brand="item"
                  :surveyID="surveyID"
                  :mediaType="'video'"
                  @set-brand-ad-video="setBrandAdVideo($event, index)"
                  v-show="item.IsSelected"
                />
              </template>
              <template v-else>
                <UploadAdvertisingVideo
                  :surveyID="surveyID"
                  @set-video-url="videoURL = $event"
                  :videoURL="videoURL"
                />
              </template>
            </div>
          </div>

          <!-- STEP: UPLOAD -->
          <div class="box-wrapper" v-else-if="activeStepKey === 'upload'">
            <StimuliTitle
              title="Please upload the imagery you want to show for each cell you've selected."
            />
            <div>
              <ChosenBrandItem
                v-for="(item, index) in brands"
                :key="index"
                :brand="item"
                :surveyID="surveyID"
                @set-brand-image="setBrandImage($event, index)"
                v-show="item.IsSelected"
              />
            </div>
          </div>
          <!-- <div class="box-wrapper" v-if="currentStep === 1">
            <StimuliTitle
              title="Select the brands that you want to run maps for. A cell will be created for each brand you select. Please ensure you upload the image that you want respondents to see for the exercise."
            ></StimuliTitle>

            <Loader
              class="mt-35"
              text="Loading brands..."
              v-if="loadingBrands"
            ></Loader>
            <div v-else class="management-system__grid">
              <BrandItem
                v-for="(item, index) in brands"
                :key="index"
                :brand="item"
                :loading="loadingBrands"
                @item-click="selectBrand(index)"
              ></BrandItem>
            </div>
          </div>
          <div class="box-wrapper" v-else>
            <StimuliTitle
              title="Please upload the imagery you want to show for each cell/brand you've selected."
            ></StimuliTitle>
            <div>
              <ChosenBrandItem
                v-for="(item, index) in brands"
                :key="index"
                :brand="item"
                :surveyID="surveyID"
                @set-brand-image="setBrandImage($event, index)"
                v-show="item.IsSelected"
              ></ChosenBrandItem>
            </div>
          </div> -->

          <StepNaviBar
            :nextDisable="nextDisable"
            :conciergeAssistance="true"
            @prevHandler="prevStep"
            @nextHandler="nextStep"
            stepsType="map-maker-test"
            @editable-survey-updated="handleEditableSurveyUpdate"
            @loading-survey-data="loadingBrands = $event"
            :loadingNextBtn="saveBrandsLoading"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import * as jsUtils from "@/utils/jsUtils.js";
import * as wsUtils from "@/utils/wsUtils.js";
import StimuliTitle from "@/components/packTestSurvey/StimuliTitle.vue";
import IconBase from "@/components/general/IconBase.vue";
import IconChartHistogram from "@/components/icons/IconChartHistogram.vue";
import ProgressBar from "@/components/videoAds/ProgressBar.vue";
import BrandItem from "@/components/mapMakerTest/BrandItem.vue";
import UploadAdvertisingVideo from "@/components/mapMakerTest/UploadAdvertisingVideo.vue";
import UploadAdvertisingImage from "@/components/mapMakerTest/UploadAdvertisingImage.vue";
import ChosenBrandItem from "@/components/mapMakerTest/ChosenBrandItem.vue";
import ChooseBrandAdItem from "@/components/mapMakerTest/ChooseBrandAdItem.vue";
import StepNaviBar from "@/components/videoAds/StepNaviBar1.vue";
import Loader from "@/components/general/Loader.vue";
import surveyPriceMixin from "@/mixins/surveyPriceCalculation.js";

export default {
  name: "StimuliMapMaker",
  components: {
    StimuliTitle,
    IconBase,
    IconChartHistogram,
    ProgressBar,
    BrandItem,
    StepNaviBar,
    Loader,
    ChosenBrandItem,
    ChooseBrandAdItem,
    UploadAdvertisingVideo,
    UploadAdvertisingImage,
  },
  props: {},
  data() {
    return {
      client: {},
      user: null,
      brands: [],
      surveyID: null,
      loadingBrands: true,
      currentStep: null,
      saveBrandsLoading: false,
      personaType: null,
      cellType: null,
      mediaType: null,
      currentStepIndex: 0,
      videoURL: null,
      imageURL: null,
      editableSurvey: false,
    };
  },
  mixins: [surveyPriceMixin],
  computed: {
    disableNextStep() {
      return true;
    },
    entityType() {
      const typeOfEntities = this.editableSurvey?.SurveyConfig?.typeOfEntities;

      if (
        typeOfEntities &&
        typeOfEntities !== null &&
        typeOfEntities.trim() !== ""
      ) {
        return typeOfEntities.toLowerCase();
      }

      return "brands";
    },
    nextDisable() {
      if (this.activeStepKey === "brands") {
        return this.brands.filter((item) => item.IsSelected).length === 0;
      }
      if (this.activeStepKey === "upload") {
        return (
          this.brands.filter((item) => item.IsSelected).length === 0 ||
          this.brands.some((item) => item.IsSelected && !item.logoURL)
        );
      }
      if (this.activeStepKey === "media-image") {
        if (this.personaType === "new" && this.cellType === "multi") {
          return (
            this.brands.filter((item) => item.IsSelected).length === 0 ||
            this.brands.some(
              (item) => item.IsSelected && !item.advertisingImageURL
            )
          );
        }
        return !this.imageURL;
      }
      if (this.activeStepKey === "media-video") {
        if (this.personaType === "new" && this.cellType === "multi") {
          return (
            this.brands.filter((item) => item.IsSelected).length === 0 ||
            this.brands.some(
              (item) => item.IsSelected && !item.advertisingVideoURL
            )
          );
        }
        return !this.videoURL;
      }
      return false;
    },
    selectedBrands() {
      return this.brands.filter((item) => item.IsSelected);
    },
    progressBarStatus() {
      const selectedBrand =
        this.brands.filter((item) => item.IsSelected).length !== 0;
      const imageUploaded = this.brands.some(
        (item) => item.IsSelected && item.logoURL
      );

      if (this.mediaType === "image") {
        return [selectedBrand, imageUploaded, !!this.imageURL];
      }

      if (this.mediaType === "video") {
        return [selectedBrand, imageUploaded, !!this.videoURL];
      }

      if (this.mediaType === "image&video") {
        return [selectedBrand, imageUploaded, !!this.imageURL, !!this.videoURL];
      }

      // default / "none"
      return [selectedBrand, imageUploaded];
    },
    orderedSteps() {
      const steps = ["brands", "upload"];

      if (this.mediaType === "image") {
        steps.push("media-image");
      } else if (this.mediaType === "video") {
        steps.push("media-video");
      } else if (this.mediaType === "image&video") {
        steps.push("media-image", "media-video");
      }

      return steps;
    },
    activeStepKey() {
      return this.orderedSteps[this.currentStepIndex] || "brands";
    },
    pageSubtitle() {
      switch (this.activeStepKey) {
        case "brands":
          return `Select ${this.entityType}`;
        case "media-image":
          return "Upload advertising image";
        case "media-video":
          return "Upload advertising video";
        case "upload":
          return "Upload images";
        default:
          return "";
      }
    },
  },
  watch: {},
  methods: {
    // applyPersonaSelection() {
    //   const isNewPersona = this.personaType === "new";

    //   if (isNewPersona) {
    //     this.brands.forEach((brand, index) => {
    //       this.$set(this.brands, index, {
    //         ...brand,
    //         IsSelected: true,
    //       });
    //     });
    //     this.currentStep = 2;
    //   } else {
    //     this.currentStep = 1;
    //   }
    // },
    applyPersonaSelection() {
      const isNewPersona = this.personaType === "new";
      if (isNewPersona) {
        this.brands.forEach((brand, index) => {
          this.$set(this.brands, index, { ...brand, IsSelected: true });
        });
        // Skoči direktno na 'upload' ako postoji; inače ostani na 'brands'
        const uploadIndex = this.orderedSteps.indexOf("upload");
        this.currentStepIndex = uploadIndex !== -1 ? uploadIndex : 0;
      } else {
        this.currentStepIndex = 0; // start at brands
      }
    },
    handleEditableSurveyUpdate(surveyData) {
      if (surveyData && surveyData.SurveyConfig && surveyData.brands) {
        this.brands = surveyData.brands;
        this.personaType = surveyData.SurveyConfig.personaType || null;
        this.cellType = surveyData.SurveyConfig.cellType || null;
        this.mediaType = surveyData.SurveyConfig.mediaType || null;
        this.videoURL = surveyData.SurveyConfig.videoLink || null;
        this.imageURL = surveyData.SurveyConfig.imageURL || null;
        this.typeOfEntities = surveyData.SurveyConfig.typeOfEntities || null;

        this.applyPersonaSelection();
      } else {
        this.brands = [];
        this.personaType = null;
        this.cellType = null;
        this.mediaType = null;
      }
    },
    selectBrand(index) {
      this.brands[index].IsSelected = !this.brands[index].IsSelected;
      this.saveBrands();
    },
    setBrandAdImage(imageURL, index) {
      this.brands[index].advertisingImageURL = imageURL;
      this.saveBrands();
    },
    setBrandAdVideo(videoURL, index) {
      this.brands[index].advertisingVideoURL = videoURL;
      this.saveBrands();
    },
    setBrandImage(imageURL, index) {
      this.brands[index].logoURL = imageURL;
      this.saveBrands();
    },
    saveBrands(nextStep) {
      if (nextStep) {
        this.saveBrandsLoading = true;
      }

      const data = {
        wsName: "SaveSurveyBrands",
        data: {
          surveyID: this.surveyID,
          brandData: this.brands,
          userId: this.user.EncrypteduserID,
          isSelected: false,
        },
      };

      const saveBrands = new Promise((resolve) => {
        wsUtils.ApiPost(data, resolve);
      });

      saveBrands.then((data) => {
        if (data.Success && nextStep) {
          this.$router.push({
            name: "quotas-map-maker",
            params: { id: this.surveyID },
          });
        }
        let currentSummaryData = jsUtils.getSurveySummaryData();
        jsUtils.setSurveySummaryData({
          ...currentSummaryData,
          mapMakerBrandData: this.brands,
        });
        this.getMapMakerTotalPrice();
        this.saveBrandsLoading = false;
      });
    },
    // prevStep() {
    //   if (
    //     this.currentStep === 2 &&
    //     this.personaType.toLowerCase() === "regular"
    //   ) {
    //     this.currentStep = 1;
    //   } else {
    //     this.$router.push({
    //       name: "map-maker-category-inputs",
    //       params: { id: this.surveyID },
    //     });
    //   }
    // },
    // nextStep() {
    //   if (this.currentStep === 1) {
    //     this.currentStep = 2;
    //   } else {
    //     this.saveBrands("nextStep");
    //   }
    // },

    prevStep() {
      // If we are not on the first step, just go back one step
      if (this.currentStepIndex > 0) {
        this.currentStepIndex -= 1;
        return;
      }
      // Otherwise behave as before (navigate back to previous route)
      this.$router.push({
        name: "map-maker-category-inputs",
        params: { id: this.surveyID },
      });
    },
    nextStep() {
      // If we're at the last step ("upload"), keep the old behavior: save and move on
      const isLast = this.currentStepIndex === this.orderedSteps.length - 1;
      if (isLast) {
        this.saveBrands("nextStep");
        return;
      }
      // Otherwise, just go to the next step
      this.currentStepIndex += 1;
    },
  },
  mounted() {},
  created() {},
  activated() {
    this.editableSurvey = jsUtils.getEditableSurvey();
    this.surveyID = this.$route.params.id;
    this.client = jsUtils.getCurClient();
    this.user = jsUtils.getUserInfo();
    if (!this.mediaType) this.mediaType = "none";
    this.currentStepIndex = 0;
    this.getMapMakerTotalPrice();
  },
};
</script>

<style lang="scss" scoped>
.box-wrapper {
  min-height: 200px;
}
</style>
